/**
 * "Continue with Google", bound to the tab (or app) that pressed it.
 *
 * The API's callback no longer puts the session in a URL. It redirects with a
 * one-time code that redeems only with the PKCE verifier (RFC 7636, S256) made
 * here as the button is pressed. The verifier stays in this tab's
 * sessionStorage and only its challenge leaves, so a callback link started
 * anywhere else signs nobody in here (login CSRF), and a code read from
 * history or a log is useless on its own. See the API's
 * services/auth/googleSignInBinding for the server half.
 *
 * Web and native share it: the native app starts the same sign-in in the
 * system browser and finishes it from the deep link (lib/nativeAuth).
 */
import { API_URL } from '@/config/api';
import { apiRequest } from '@/services/api/core';
import { logger } from './logger';

const PENDING_KEY = 'bbx_google_signin_pending';
/** Longer than the API keeps the sign-in's state (10 minutes), as in app/loginRedirect. */
const PENDING_MAX_AGE_MS = 15 * 60 * 1000;

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** BASE64URL(SHA-256(verifier)), the S256 challenge. */
export async function pkceChallengeOf(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

/**
 * Make a verifier, park it in this tab under `key`, and return its challenge.
 * A new press replaces an earlier verifier, so only the latest sign-in can
 * finish. The admin login (pages/admin/adminGoogleSignIn) parks its own.
 */
export async function parkPkceVerifier(key: string, now: number = Date.now()): Promise<string> {
  // 32 random bytes → 43 characters, the RFC's minimum length and full entropy.
  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
  try {
    sessionStorage.setItem(key, JSON.stringify({ verifier, at: now }));
  } catch {
    // Storage unavailable (blocked site data): the callback then finds no
    // verifier and refuses, as it does for any sign-in this tab did not start.
  }
  return pkceChallengeOf(verifier);
}

/**
 * Park this sign-in's verifier and return the URL that starts the sign-in at
 * the API with its challenge.
 */
export async function googleSignInStartUrl(
  platform: 'web' | 'mobile' = 'web',
  now: number = Date.now(),
): Promise<string> {
  const params = new URLSearchParams({ challenge: await parkPkceVerifier(PENDING_KEY, now) });
  if (platform === 'mobile') params.set('platform', 'mobile');
  return `${API_URL}/api/auth/google?${params.toString()}`;
}

/** Leave this tab for Google (web). The Login and Signup buttons. */
export async function startGoogleSignIn(): Promise<void> {
  try {
    window.location.href = await googleSignInStartUrl('web');
  } catch (err) {
    // No Web Crypto (an insecure origin): nothing could be bound, so nothing starts.
    logger.debug('[googleSignIn] could not start', err);
  }
}

/** The verifier parked under `key`, while still fresh. Removed as it is read: one attempt. */
export function takePkceVerifier(key: string, now: number = Date.now()): string | null {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
  } catch {
    return null;
  }
  if (!stored) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || !('verifier' in parsed) || !('at' in parsed)) return null;
  const { verifier, at } = parsed;
  if (typeof verifier !== 'string' || typeof at !== 'number') return null;
  const age = now - at;
  return age >= 0 && age <= PENDING_MAX_AGE_MS ? verifier : null;
}

/** The sign-in this tab started failed or was abandoned. */
export function forgetGoogleSignIn(): void {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // Nothing reachable to forget.
  }
}

export interface GoogleSession {
  token: string;
  workspaceId: string | null;
}

interface ExchangeResponse {
  success: boolean;
  data?: { token?: string; workspaceId?: string };
}

/**
 * Finish a sign-in from what the API's callback sent back: the query of
 * /auth/callback (web) or of the deep link (native). Null when this tab did
 * not start a sign-in, or the code does not redeem; nothing is stored then.
 */
export async function completeGoogleSignIn(
  params: URLSearchParams,
  now: number = Date.now(),
): Promise<GoogleSession | null> {
  const verifier = takePkceVerifier(PENDING_KEY, now);
  if (!verifier) return null;

  const code = params.get('code');
  if (code) {
    try {
      const res = await apiRequest<ExchangeResponse>('/auth/google/exchange', {
        method: 'POST',
        body: JSON.stringify({ code, verifier }),
      });
      const token = res?.data?.token;
      return token ? { token, workspaceId: res.data?.workspaceId || null } : null;
    } catch {
      return null;
    }
  }

  // TRANSITIONAL — delete once the API that mints codes is live. Vercel ships
  // this client as it merges; Render deploys the API by hand later, and until
  // then the API still returns the session itself as ?token=. It is taken only
  // for a sign-in this tab started, so a link carrying someone else's token
  // still signs nobody in.
  const token = params.get('token');
  return token ? { token, workspaceId: params.get('workspaceId') || null } : null;
}
